import { motion } from "framer-motion";
import { useNavigate } from "./NavContext";

const EASE = [0.16, 1, 0.3, 1];

const STREAKS =
  "https://images.unsplash.com/photo-1600633748503-9bc43cc48cca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdvbGQlMjBsaWdodHxlbnwwfHx8YmxhY2t8MTc4ODAyMzI3OHww&ixlib=rb-4.1.0&q=60&w=1600";

// Stable acrylic surfaces: avoid nested backdrop-filter differences between
// desktop Chromium and mobile Safari while preserving the same glass look.
const CHAIN_GLASS_STYLE = {
  backgroundColor: "#5f5f63",
  backgroundImage:
    "linear-gradient(135deg, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.16) 100%)",
  border: "1px solid rgba(255,255,255,0.38)",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.5), 0 12px 30px -12px rgba(0,0,0,0.6)",
};

const SECONDARY_GLASS_STYLE = {
  backgroundColor: "#252529",
  backgroundImage:
    "linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.06) 100%)",
  border: "1px solid rgba(255,255,255,0.3)",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.42), 0 12px 30px -12px rgba(0,0,0,0.6)",
};

const Pill = ({ label, icon }) => (
  <span
    style={CHAIN_GLASS_STYLE}
    className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-accent text-[13px] font-medium text-white/90"
  >
    <span className="flex h-4 w-4 items-center justify-center">
      {icon}
    </span>
    <span>{label}</span>
  </span>
);

export const Hero = () => {
  const navigate = useNavigate();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden bg-black px-4 pb-20 pt-28 md:px-8 md:pt-32"
    >
      {/* Background light streaks */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-20 bg-cover bg-center opacity-60"
        style={{ backgroundImage: `url(${STREAKS})` }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-black/50 via-transparent to-black"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute right-[4%] top-1/2 -z-10 h-[70vh] w-[46vh] -translate-y-1/2 rounded-full bg-[#6C63FF]/16 blur-[150px]"
      />

      <div className="relative z-10 mx-auto w-full max-w-6xl">
        <div className="relative">
          {/* ===== Big liquid-glass card ===== */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="lg-surface relative overflow-hidden rounded-[2rem] px-8 py-12 md:rounded-[2.5rem] md:px-14 md:py-16 lg:min-h-[560px]"
          >
            <div className="lg:grid lg:grid-cols-[58%_42%] lg:items-center">
              <div className="lg:pr-8">
                {/* Pills at the top */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
                  className="flex flex-wrap gap-2.5"
                >
                  <Pill
                    label="Fragoso Smart Chain"
                    icon={<span className="h-2 w-2 rounded-full bg-emerald-400" />}
                  />
                </motion.div>

                {/* Heading — bold sans, two-tone */}
                <motion.h1
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.28, ease: EASE }}
                  className="mt-7 font-body text-5xl font-bold leading-[0.98] tracking-tight text-white md:text-6xl lg:text-[4.6rem]"
                >
                  Tu valor.
                  <br />
                  <span className="text-white/55">Privado. Instantáneo.</span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
                  className="mt-6 max-w-md font-body text-base leading-relaxed text-white/72 md:text-lg"
                >
                  La arquitectura privada para operar MXF, cobrar desde tu web,
                  certificar documentos y tokenizar activos reales. Todo
                  verificable, en segundos.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
                  className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
                >
                  <button
                    onClick={() => navigate("acceso")}
                    data-testid="hero-cta-primary"
                    className="inline-flex items-center justify-center rounded-full bg-gold px-8 py-4 font-accent text-sm text-white transition-all duration-300 hover:bg-gold-light active:scale-[0.98]"
                  >
                    Solicitar acceso
                  </button>
                  <button
                    onClick={() => navigate("pagos")}
                    data-testid="hero-cta-secondary"
                    style={SECONDARY_GLASS_STYLE}
                    className="rounded-full px-8 py-4 font-accent text-sm text-white transition-transform duration-300 active:scale-[0.98]"
                  >
                    Descubrir cómo funciona
                  </button>
                </motion.div>

                {/* Phone on mobile (inside card, below text) */}
                <div className="mt-10 flex justify-center lg:hidden">
                  <img
                    src="/mockups/aurexis_hero.webp"
                    alt="App Aurexis"
                    draggable={false}
                    loading="lazy"
                    decoding="async"
                    className="w-[62%] max-w-[240px] select-none drop-shadow-[0_40px_70px_rgba(0,0,0,0.6)]"
                  />
                </div>
              </div>

              {/* Phone centered and fully contained by the glass card on desktop */}
              <motion.div
                initial={{ opacity: 0, x: 30, scale: 0.96 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
                className="pointer-events-none relative hidden h-full min-h-[440px] items-center justify-center lg:flex"
              >
                <div
                  aria-hidden
                  className="absolute left-1/2 top-1/2 h-[70%] w-[70%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#6C63FF]/22 blur-[110px]"
                />
                <img
                  src="/mockups/aurexis_hero.webp"
                  alt="App Aurexis: saldo en MXF, servicios y actividad reciente"
                  data-testid="hero-phone"
                  draggable={false}
                  decoding="async"
                  fetchPriority="high"
                  className="relative max-h-[500px] w-auto max-w-full select-none object-contain drop-shadow-[0_42px_80px_rgba(0,0,0,0.68)] xl:max-h-[540px]"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
